import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import type { DeskStat, RedeemAppWithStats, RedeemPostCounts, ScheduleItem, Target } from '@/api/client'
import { AppName } from '@/lib/AppName'
import { TONE_CLASS, type Tone } from '@/lib/status'
import { cn } from '@/lib/utils'
import { TonePill, When } from '@/ui'
import { PostLine, byAttention } from '../schedule/PostLine'
import { APP_PLATFORM, AppStatusSelect, Expiry } from './shared'

export const NO_POSTS: RedeemPostCounts = { draft: 0, scheduled: 0, published: 0, failed: 0 }

/** Not yet up: written and waiting for a slot, or booked. */
export const ready = (p: RedeemPostCounts) => p.draft + p.scheduled

/** A pool this low is the next thing to refill. */
const LOW_POOL = 20

/** How many of an app's posts the card lists before 모두 보기. */
const SHOWN_POSTS = 4

export function DeskSummary({ app, targets }: { app: RedeemAppWithStats['app']; targets: Target[] }) {
  const account = targets.find((t) => t.id === app.target_id)
  // A running app that waits for a person says so out loud: 가동 next to a
  // grey 수동 read as "sending" (Dazzle Cam, 2026-10-18).
  const waits = app.auto_handout !== 1 && app.status === 'active' && !app.ended_at
  return (
    <span className="text-table">
      {account ? account.name : <span className="text-muted-foreground">계정 미지정</span>}
      <span className="text-muted-foreground">
        {' · '}
        {app.auto_handout === 1 ? (
          '자동 발송'
        ) : (
          <span className={cn(waits && cn('font-medium', TONE_CLASS.publishing.text))}>수동 발송</span>
        )}
        {' · '}
        {app.daily_cap || 20}/일
      </span>
    </span>
  )
}

/** The pool as a bar: what is left in the tone that says whether to refill,
 *  what went out in grey, voided codes in red. */
function PoolBar({ name, total, available, issued, voided }: { name: string; total: number; available: number; issued: number; voided: number }) {
  const tone: Tone = available === 0 ? 'failed' : available < LOW_POOL ? 'publishing' : 'published'
  const pct = (n: number) => (total > 0 ? `${(n / total) * 100}%` : '0%')
  return (
    <div
      role="meter"
      aria-label={`${name} 남은 코드`}
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={available}
      className="flex h-2 overflow-hidden rounded-full bg-muted"
    >
      <span className={TONE_CLASS[tone].dot} style={{ width: pct(available) }} />
      <span className="bg-muted-foreground/30" style={{ width: pct(issued) }} />
      <span className={cn(TONE_CLASS.failed.dot, 'opacity-50')} style={{ width: pct(voided) }} />
    </div>
  )
}

/** One of today's desk numbers, in its box. Zero is greyed: the boxes that
 *  matter are the ones with something in them. */
function Count({ label, n, tone }: { label: string; n: number; tone?: string }) {
  return (
    <div className="rounded-md bg-muted/60 px-2 py-1.5 text-center">
      <div className={cn('text-body font-semibold tabular-nums', n === 0 ? 'text-muted-foreground/60' : tone)}>{n}</div>
      <div className="text-micro text-muted-foreground">{label}</div>
    </div>
  )
}

/** One app on the board: what it gives away, how much of its pool is left,
 *  what the desk did with it today, and its promo posts — where each goes and
 *  when. */
export function AppCard({
  row,
  apps,
  targets,
  desk,
  posts,
  postsLoading,
}: {
  row: RedeemAppWithStats
  apps: RedeemAppWithStats['app'][]
  targets: Target[]
  desk?: DeskStat
  /** The app's posts from the schedule: booked, waiting and recent. */
  posts: ScheduleItem[]
  postsLoading: boolean
}) {
  const { app, stats } = row
  const counts = row.posts ?? NO_POSTS
  const running = app.status === 'active' && !app.ended_at
  const n = ready(counts)
  const lines = posts.slice().sort(byAttention)
  const total = counts.draft + counts.scheduled + counts.published + counts.failed

  return (
    <article
      aria-label={app.name}
      className={cn('flex w-full flex-col gap-4 rounded-lg border border-border bg-card p-4', app.status === 'archived' && 'opacity-80')}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Link to={`/redeem/${app.id}`} className="text-body font-semibold hover:underline">
              <AppName app={app} apps={apps} />
            </Link>
            {app.ended_at && <TonePill tone="neutral" label="나눔 종료" />}
          </div>
          <div className="text-meta text-muted-foreground">
            {app.slug} · {APP_PLATFORM[app.platform]}
            {app.free_until && ` · 앱 무료 ${app.free_until}까지`}
          </div>
        </div>
        <AppStatusSelect app={app} className="shrink-0" />
      </header>

      <div className="text-table">
        <span className="text-muted-foreground">해제 </span>
        {app.product_name || '—'}
        {/* A free period is not the same offer as a permanent unlock, and
            the difference decides what the DM has to disclose. */}
        {app.grant_kind === 'trial' && (
          <span className={cn('ml-1.5 text-meta', TONE_CLASS.publishing.text)}>
            {app.renewal_price} → {app.grant_period} 무료
          </span>
        )}
      </div>

      <section aria-label="코드 풀" className="space-y-1.5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-meta font-medium text-muted-foreground">코드</span>
          <span className="text-table tabular-nums">
            <span
              className={cn(
                'text-section font-semibold',
                stats.available === 0 ? TONE_CLASS.failed.text : stats.available < LOW_POOL && TONE_CLASS.publishing.text,
              )}
            >
              {stats.available}
            </span>
            <span className="text-muted-foreground"> / {stats.total} 남음</span>
          </span>
        </div>
        <PoolBar name={app.name} total={stats.total} available={stats.available} issued={stats.issued} voided={stats.void} />
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-meta text-muted-foreground">
          <span className="tabular-nums">
            나감 {stats.issued} · 오늘 {stats.issued_today}
            {stats.void > 0 && ` · 무효 ${stats.void}`}
          </span>
          {app.expires_on && (
            <span>
              만료 <Expiry date={app.expires_on} />
            </span>
          )}
        </div>
      </section>

      <section aria-label="오늘 데스크" className="space-y-1.5">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
          <span className="text-meta font-medium text-muted-foreground">오늘 데스크</span>
          <DeskSummary app={app} targets={targets} />
        </div>
        <div className="grid grid-cols-4 gap-1.5">
          <Count label="요청" n={desk?.requests_today ?? 0} />
          <Count label="발송" n={desk?.issued_today ?? stats.issued_today} tone="text-foreground" />
          <Count label="대기" n={desk?.pending_new ?? 0} tone="text-accent" />
          <Count label="실패" n={desk?.failed_open ?? 0} tone={TONE_CLASS.failed.text} />
        </div>
        {desk && (desk.issued_window > 0 || desk.last_issued_at) && (
          <div className="text-meta text-muted-foreground">
            7일 발송 <span className="tabular-nums">{desk.issued_window}</span>
            {desk.last_issued_at && (
              <>
                {' · 마지막 '}
                <When iso={desk.last_issued_at} />
              </>
            )}
          </div>
        )}
      </section>

      <section aria-label="프로모션 글" className="flex flex-1 flex-col gap-1.5">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3">
          <span className="text-meta font-medium text-muted-foreground">프로모션 글</span>
          <span className="text-meta tabular-nums text-muted-foreground">
            {n > 0 ? (
              <span className="text-table font-semibold text-foreground">준비 {n}</span>
            ) : (
              <span className={cn('text-table', running ? cn('font-medium', TONE_CLASS.publishing.text) : 'text-muted-foreground')}>
                준비된 글 없음
              </span>
            )}
            {' · '}예약 {counts.scheduled} · 초안 {counts.draft} · 게시 {counts.published}
            {counts.failed > 0 && <span className={cn('font-medium', TONE_CLASS.failed.text)}> · 실패 {counts.failed}</span>}
          </span>
        </div>
        {lines.length > 0 ? (
          <ul className="divide-y divide-border overflow-hidden rounded-md border border-border">
            {lines.slice(0, SHOWN_POSTS).map((it) => (
              <PostLine key={`${it.kind}-${it.id}`} it={it} />
            ))}
          </ul>
        ) : (
          !postsLoading && (
            <p className="rounded-md border border-dashed border-border px-2.5 py-3 text-center text-meta text-muted-foreground">
              {total > 0 ? '최근 30일과 앞으로 잡힌 글이 없다' : '아직 쓴 글이 없다'}
            </p>
          )
        )}
        <Link
          to={`/redeem/${app.id}/posts`}
          className="mt-auto inline-flex items-center gap-1 self-start pt-1 text-meta font-medium text-accent hover:underline"
        >
          {total > 0 ? `글 ${total}개 모두 보기` : '글 쓰기'}
          <ArrowRight size={12} aria-hidden />
        </Link>
      </section>
    </article>
  )
}
